import React from "react";
import useTheme from "../../lib/useTheme";
import styled from "styled-components";
import { mergeStrings } from "../../lib/helpers";
import useScreenDimensions from "../../lib/hooks/useScreenDimensions";

type SliderSize = "small" | "medium";

export interface DGA_SliderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "style"> {
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  size?: SliderSize;
  percentage?: number;
  onChnage?: Function;
}

const NormalSlider: React.FC<DGA_SliderProps> = ({
  label = "",
  helperText = "",
  size = "medium",
  percentage = 0,
  onChnage,
  ...props
}) => {
  const theme = useTheme();
  const screenDimensions = useScreenDimensions();

  const sliderBaseRef = React.useRef<HTMLDivElement>(null);
  const sliderIndicatorRef = React.useRef<HTMLDivElement>(null);
  const [isMoving, setIsMoving] = React.useState(false);
  const [currentX, setCurrentX] = React.useState(0);
  const [canMoveFromTo, setCanMoveFromTo] = React.useState([0, 0]);

  const indicatorWidth = size === "medium" ? 16 : 12;

  React.useEffect(() => {
    const percentageResult = ((currentX - canMoveFromTo[0]) * 100) / (canMoveFromTo[1] - canMoveFromTo[0]);

    const result = Math.round(percentageResult);
    onChnage?.(result);
  }, [currentX, canMoveFromTo]);

  React.useEffect(() => {
    if (sliderBaseRef.current && sliderIndicatorRef.current) {
      const sliderRect = sliderBaseRef.current.getBoundingClientRect();
      const indicatorRect = sliderIndicatorRef.current.getBoundingClientRect();

      setCanMoveFromTo([sliderRect.left, sliderRect.right]);
      setCurrentX(indicatorRect.x + indicatorWidth / 2);
    }
  }, [sliderBaseRef, sliderIndicatorRef, screenDimensions]);

  React.useEffect(() => {
    if (isMoving) {
      const mouseMoveHandler = (e: MouseEvent) => {
        const newClientX = e.clientX;

        console.log("newClientX a", newClientX);
        if (newClientX > canMoveFromTo[0] && newClientX < canMoveFromTo[1]) {
          setCurrentX(newClientX);
        }

        if (newClientX <= canMoveFromTo[0]) {
          setCurrentX(canMoveFromTo[0]);
        }
        if (newClientX >= canMoveFromTo[1]) {
          setCurrentX(canMoveFromTo[1]);
        }
      };

      const mouseUpHandler = () => {
        document.getElementsByTagName("body")[0].classList.remove("dgauiDragging");
        setIsMoving(false);
      };

      window.addEventListener("mousemove", mouseMoveHandler);
      window.addEventListener("mouseup", mouseUpHandler);

      return () => {
        window.removeEventListener("mousemove", mouseMoveHandler);
        window.removeEventListener("mouseup", mouseUpHandler);
      };
    }
  }, [isMoving, screenDimensions, window.innerWidth]);

  return (
    <StyledComponent
      $size={size}
      $theme={theme}
      $percentage={percentage}
      $indicatorWidth={indicatorWidth}
      className={mergeStrings("dgaui dgaui_slider", props.className)}
      {...props}
    >
      {label && <label>{label}</label>}
      <div className="sliderBaseContent">
        <div className="dgaui sliderBase" ref={sliderBaseRef}>
          <div
            ref={sliderIndicatorRef}
            className="dgaui sliderIndicator"
            onMouseDown={() => {
              document.getElementsByTagName("body")[0].classList.add("dgauiDragging");
              setIsMoving(true);
            }}
          />
        </div>
        <div className="dgaui_percentageText">{percentage}</div>
      </div>
      {helperText && <div className="helperText">{helperText}</div>}
    </StyledComponent>
  );
};

const StyledComponent = styled.div<{
  $theme: Theme;
  $size: SliderSize;
  $percentage: number;
  $indicatorWidth: number;
}>`
  direction: ${(p) => p.$theme.direction};
  width: 100%;

  .sliderBaseContent {
    display: flex;
    align-items: center;
  }

  .dgaui_percentageText {
    min-width: 40px;
    text-align: center;
  }

  .sliderBase {
    flex: 1;
    margin: 8px 0;
    height: ${(p) => (p.$size === "medium" ? 8 : 4)}px;
    background-color: ${(p) => p.$theme.palette.neutral[100]};
    border-radius: 4px;
    position: relative;
    margin-inline-end: 12px;

    &::before {
      position: absolute;
      display: block;
      content: "";
      top: 0;
      left: 0;
      z-index: 1;
      border-radius: 4px;
      height: ${(p) => (p.$size === "medium" ? 8 : 4)}px;
      background-color: ${(p) => p.$theme.palette.primary[600]};
      width: ${(p) => (p.$percentage <= 100 ? p.$percentage : 100)}%;
    }

    .sliderIndicator {
      -webkit-user-select: none; /* Safari */
      -ms-user-select: none; /* IE 10+ */
      user-select: none; /* Standard syntax */

      width: ${(p) => (p.$size === "medium" ? 16 : 12)}px;
      height: ${(p) => (p.$size === "medium" ? 16 : 12)}px;
      border-radius: 100%;
      background-color: ${(p) => p.$theme.palette.primary[600]};
      position: absolute;
      cursor: pointer;
      z-index: 1;
      top: calc(50% - ${(p) => p.$indicatorWidth / 2}px);
      ${(p) => `
        left: calc(
          ${p.$percentage <= 100 ? p.$percentage : 100}% - ${p.$indicatorWidth / 2}px
        );
      `}

      &:active {
        cursor: grabbing;
      }
    }
  }
`;

export default NormalSlider;
